// Thin wrapper around @aws-sdk/client-cognito-identity-provider.
//
// Option A scope — full flows the 8 designer-spec auth screens need:
//   - signIn(email, password)                    → OK | MFA_CHALLENGE | NEW_PASSWORD_REQUIRED | MFA_SETUP_REQUIRED
//   - respondToNewPasswordChallenge(...)         → OK | MFA_CHALLENGE | MFA_SETUP_REQUIRED
//   - submitMfaCode(session, email, code)        → OK
//   - associateSoftwareToken(session)            → { secretCode, session }   (returns the TOTP seed for the QR)
//   - verifySoftwareToken(session, code)         → { session }                (proves user has the seed working)
//   - completeMfaSetup(session, email)           → OK | MFA_CHALLENGE        (finalizes the in-login MFA_SETUP challenge)
//   - forgotPassword(email)                      → void                       (never throws on user-not-found — no enumeration)
//   - confirmForgotPassword(email, code, pw)     → void
//   - refreshTokens(refreshToken)                → new tokens
//   - signOut(accessToken)                       → best-effort
//
// All session tokens are short-lived Cognito challenge sessions — held in
// memory by useAuthFlow only, never persisted (auth handoff §5).
//
// Errors are normalized through CognitoAuthError so the state machine + UI
// branch on stable string codes, not exception class names.

import {
  AssociateSoftwareTokenCommand,
  CognitoIdentityProviderClient,
  ConfirmForgotPasswordCommand,
  ForgotPasswordCommand,
  GlobalSignOutCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  VerifySoftwareTokenCommand,
  type AuthenticationResultType,
} from '@aws-sdk/client-cognito-identity-provider';
import { env } from '../config/env.js';

const client = new CognitoIdentityProviderClient({ region: env.cognitoRegion });

// ─── Error model ────────────────────────────────────────────────────────────

export type CognitoAuthErrorCode =
  | 'INVALID_CREDENTIALS'  // wrong email or password (no enumeration)
  | 'WRONG_CODE'           // MFA code, password-reset code, etc. didn't match
  | 'WEAK_PASSWORD'        // policy violation on a new password (length, complexity, etc.)
  | 'RATE_LIMITED'         // Cognito throttled (forgot-password, repeated bad attempts)
  | 'USER_NOT_CONFIRMED'   // email verification incomplete (rare for admin-invited users)
  | 'EXPIRED_SESSION'      // the challenge session expired (user took too long mid-flow)
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

export class CognitoAuthError extends Error {
  readonly code: CognitoAuthErrorCode;
  readonly originalError?: unknown;

  constructor(message: string, code: CognitoAuthErrorCode, originalError?: unknown) {
    super(message);
    this.name = 'CognitoAuthError';
    this.code = code;
    this.originalError = originalError;
  }
}

// ─── Token model ────────────────────────────────────────────────────────────

export interface SuccessTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  /** Unix epoch ms at which the access/id tokens expire. */
  expiresAt: number;
}

// ─── Auth-flow result discriminated union ───────────────────────────────────
//
// `signIn` and any challenge response may resolve to one of these. The state
// machine branches on `.kind` and renders the matching screen.

export interface OkResult {
  kind: 'OK';
  tokens: SuccessTokens;
}

export interface MfaChallengeResult {
  kind: 'MFA_CHALLENGE';
  /** Short-lived session token — pass to submitMfaCode. */
  session: string;
  /** The username (email) Cognito recognized. Use this in the challenge response. */
  email: string;
  method: 'SOFTWARE_TOKEN_MFA';
}

export interface NewPasswordRequiredResult {
  kind: 'NEW_PASSWORD_REQUIRED';
  /** Short-lived session — pass to respondToNewPasswordChallenge. */
  session: string;
  /** USER_ID_FOR_SRP from Cognito; use as USERNAME in the response. */
  email: string;
}

export interface MfaSetupRequiredResult {
  kind: 'MFA_SETUP_REQUIRED';
  /** Short-lived session — pass to associateSoftwareToken. */
  session: string;
  email: string;
}

export type SignInResult =
  | OkResult
  | MfaChallengeResult
  | NewPasswordRequiredResult
  | MfaSetupRequiredResult;

// ─── Internals ──────────────────────────────────────────────────────────────

function tokensFromResult(result: AuthenticationResultType): SuccessTokens {
  if (!result.AccessToken || !result.IdToken || !result.RefreshToken || !result.ExpiresIn) {
    throw new CognitoAuthError(
      'Cognito returned an incomplete AuthenticationResult',
      'UNKNOWN',
      result,
    );
  }
  return {
    accessToken: result.AccessToken,
    idToken: result.IdToken,
    refreshToken: result.RefreshToken,
    expiresAt: Date.now() + result.ExpiresIn * 1000,
  };
}

function tokensFromResultPartial(
  result: AuthenticationResultType,
  fallbackRefresh: string,
): SuccessTokens {
  // Some challenge responses (MFA, etc.) don't include a refresh token on the
  // final AuthenticationResult — the original refresh was issued earlier in
  // the flow. Use the fallback when missing.
  if (!result.AccessToken || !result.IdToken || !result.ExpiresIn) {
    throw new CognitoAuthError('Cognito returned an incomplete result', 'UNKNOWN', result);
  }
  return {
    accessToken: result.AccessToken,
    idToken: result.IdToken,
    refreshToken: result.RefreshToken ?? fallbackRefresh,
    expiresAt: Date.now() + result.ExpiresIn * 1000,
  };
}

function normalizeError(err: unknown): CognitoAuthError {
  if (err instanceof CognitoAuthError) return err;
  const name = (err as { name?: string }).name ?? '';
  switch (name) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      // Per auth handoff §3.1: never reveal which field was wrong.
      return new CognitoAuthError('Invalid email or password', 'INVALID_CREDENTIALS', err);
    case 'CodeMismatchException':
    case 'ExpiredCodeException':
      return new CognitoAuthError('That code didn’t work. Try again.', 'WRONG_CODE', err);
    case 'InvalidPasswordException':
      return new CognitoAuthError(
        'That password doesn’t meet the policy requirements.',
        'WEAK_PASSWORD',
        err,
      );
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return new CognitoAuthError(
        'Too many attempts. Wait a moment and try again.',
        'RATE_LIMITED',
        err,
      );
    case 'UserNotConfirmedException':
      return new CognitoAuthError('Email verification is incomplete.', 'USER_NOT_CONFIRMED', err);
    case 'NetworkError':
    case 'TimeoutError':
      return new CognitoAuthError('Network error. Try again in a moment.', 'NETWORK_ERROR', err);
    default:
      return new CognitoAuthError(
        (err as Error).message ?? 'Unknown auth error',
        'UNKNOWN',
        err,
      );
  }
}

/**
 * Map a Cognito InitiateAuth / RespondToAuthChallenge response onto a typed
 * SignInResult. `originalEmail` is what the caller passed; used as USERNAME
 * if Cognito's response doesn't surface a USER_ID_FOR_SRP challenge param.
 */
function mapChallengeResponse(
  response: {
    AuthenticationResult?: AuthenticationResultType | undefined;
    ChallengeName?: string | undefined;
    Session?: string | undefined;
    ChallengeParameters?: Record<string, string> | undefined;
  },
  originalEmail: string,
): SignInResult {
  if (response.AuthenticationResult) {
    return { kind: 'OK', tokens: tokensFromResult(response.AuthenticationResult) };
  }

  const session = response.Session;
  if (!session) {
    throw new CognitoAuthError(
      'Cognito returned a challenge with no session',
      'UNKNOWN',
      response,
    );
  }

  // Always prefer the email the user typed at login. Cognito returns
  // USER_ID_FOR_SRP in challenge parameters but, for our pool config
  // (username == sub, set by AdminCreateUser), that value is the sub —
  // which is NOT what we want to surface in the UI ("ACCOUNT · GC COACH
  // (6255c434-3021-…)" instead of the email). originalEmail is the
  // string the user typed at the login form; that's the right human-
  // readable identifier to thread through the rest of the auth flow.
  const email = originalEmail;

  switch (response.ChallengeName) {
    case 'SOFTWARE_TOKEN_MFA':
      return { kind: 'MFA_CHALLENGE', session, email, method: 'SOFTWARE_TOKEN_MFA' };
    case 'NEW_PASSWORD_REQUIRED':
      return { kind: 'NEW_PASSWORD_REQUIRED', session, email };
    case 'MFA_SETUP':
      return { kind: 'MFA_SETUP_REQUIRED', session, email };
    default:
      throw new CognitoAuthError(
        `Unsupported auth challenge: ${response.ChallengeName ?? 'unknown'}`,
        'UNKNOWN',
        response,
      );
  }
}

// ─── Sign-in entry ──────────────────────────────────────────────────────────

export async function signIn(email: string, password: string): Promise<SignInResult> {
  try {
    const response = await client.send(
      new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: env.cognitoAppClientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      }),
    );
    return mapChallengeResponse(response, email);
  } catch (err) {
    throw normalizeError(err);
  }
}

// ─── NEW_PASSWORD_REQUIRED flow ─────────────────────────────────────────────

export async function respondToNewPasswordChallenge(
  session: string,
  email: string,
  newPassword: string,
): Promise<SignInResult> {
  try {
    const response = await client.send(
      new RespondToAuthChallengeCommand({
        ClientId: env.cognitoAppClientId,
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        Session: session,
        ChallengeResponses: {
          USERNAME: email,
          NEW_PASSWORD: newPassword,
        },
      }),
    );
    return mapChallengeResponse(response, email);
  } catch (err) {
    throw normalizeError(err);
  }
}

// ─── MFA challenge (login-time) ─────────────────────────────────────────────

export async function submitMfaCode(
  session: string,
  email: string,
  code: string,
): Promise<SuccessTokens> {
  try {
    const response = await client.send(
      new RespondToAuthChallengeCommand({
        ClientId: env.cognitoAppClientId,
        ChallengeName: 'SOFTWARE_TOKEN_MFA',
        Session: session,
        ChallengeResponses: {
          USERNAME: email,
          SOFTWARE_TOKEN_MFA_CODE: code,
        },
      }),
    );
    if (!response.AuthenticationResult) {
      throw new CognitoAuthError(
        'MFA verification did not return tokens',
        'UNKNOWN',
        response,
      );
    }
    return tokensFromResult(response.AuthenticationResult);
  } catch (err) {
    throw normalizeError(err);
  }
}

// ─── MFA enrollment (during login when challenge is MFA_SETUP) ──────────────

export interface AssociateTokenResult {
  /** TOTP seed (base32). Render into a QR with the otpauth:// URL. */
  secretCode: string;
  /** New session — pass to verifySoftwareToken next. */
  session: string;
}

export async function associateSoftwareToken(session: string): Promise<AssociateTokenResult> {
  try {
    const response = await client.send(new AssociateSoftwareTokenCommand({ Session: session }));
    if (!response.SecretCode || !response.Session) {
      throw new CognitoAuthError(
        'AssociateSoftwareToken returned an incomplete response',
        'UNKNOWN',
        response,
      );
    }
    return { secretCode: response.SecretCode, session: response.Session };
  } catch (err) {
    throw normalizeError(err);
  }
}

export async function verifySoftwareToken(
  session: string,
  code: string,
): Promise<{ session: string }> {
  try {
    const response = await client.send(
      new VerifySoftwareTokenCommand({
        Session: session,
        UserCode: code,
        FriendlyDeviceName: 'GC Coach admin',
      }),
    );
    if (response.Status !== 'SUCCESS' || !response.Session) {
      throw new CognitoAuthError(
        `MFA enrollment did not succeed (Status=${response.Status ?? 'unknown'})`,
        'WRONG_CODE',
        response,
      );
    }
    return { session: response.Session };
  } catch (err) {
    throw normalizeError(err);
  }
}

/**
 * Final step in the in-login MFA_SETUP flow: after VerifySoftwareToken
 * succeeds, complete the MFA_SETUP challenge to issue real tokens.
 */
export async function completeMfaSetup(session: string, email: string): Promise<SignInResult> {
  try {
    const response = await client.send(
      new RespondToAuthChallengeCommand({
        ClientId: env.cognitoAppClientId,
        ChallengeName: 'MFA_SETUP',
        Session: session,
        ChallengeResponses: {
          USERNAME: email,
        },
      }),
    );
    return mapChallengeResponse(response, email);
  } catch (err) {
    throw normalizeError(err);
  }
}

// ─── Password reset (out-of-band, no active session needed) ─────────────────

/**
 * Trigger a password-reset code email. Resolves successfully for any input
 * (including non-existent users) — Cognito itself enforces no enumeration.
 * Returns void; the screen always shows the same neutral confirmation.
 */
export async function forgotPassword(
  email: string,
  locale: 'en' | 'fr' = 'en',
): Promise<void> {
  try {
    await client.send(
      new ForgotPasswordCommand({
        ClientId: env.cognitoAppClientId,
        Username: email,
        // Forwarded verbatim to CustomEmailSender_ForgotPassword. The Lambda
        // reads `locale` to pick the rendered email language. Values MUST be
        // strings (Cognito enforces).
        ClientMetadata: { locale },
      }),
    );
  } catch (err) {
    // Surface only rate-limit (so the screen can show a countdown) and network
    // errors. Suppress UserNotFoundException — we do not reveal user existence.
    const name = (err as { name?: string }).name ?? '';
    if (
      name === 'LimitExceededException' ||
      name === 'TooManyRequestsException' ||
      name === 'NetworkError' ||
      name === 'TimeoutError'
    ) {
      throw normalizeError(err);
    }
    // For everything else (UserNotFound, NotAuthorized, etc.), swallow — screen
    // shows the same neutral confirmation message regardless.
  }
}

export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  try {
    await client.send(
      new ConfirmForgotPasswordCommand({
        ClientId: env.cognitoAppClientId,
        Username: email,
        ConfirmationCode: code,
        Password: newPassword,
      }),
    );
  } catch (err) {
    throw normalizeError(err);
  }
}

// ─── Token lifecycle ────────────────────────────────────────────────────────

export async function refreshTokens(
  refreshToken: string,
): Promise<Omit<SuccessTokens, 'refreshToken'>> {
  try {
    const response = await client.send(
      new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: env.cognitoAppClientId,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken,
        },
      }),
    );
    const result = response.AuthenticationResult;
    if (!result?.AccessToken || !result.IdToken || !result.ExpiresIn) {
      throw new CognitoAuthError('Refresh did not return tokens', 'UNKNOWN', response);
    }
    return tokensFromResultPartial(result, refreshToken);
  } catch (err) {
    throw normalizeError(err);
  }
}

export async function signOut(accessToken: string): Promise<void> {
  try {
    await client.send(new GlobalSignOutCommand({ AccessToken: accessToken }));
  } catch (err) {
    // Sign-out is best-effort; the frontend already clears local tokens. Log
    // and swallow rather than blocking the user from continuing.
    console.warn('GlobalSignOut failed (continuing local sign-out):', err);
  }
}
